/**
 * Pops — instanced point-of-presence markers on the globe surface.
 *
 * One `InstancedMesh` for the whole set: a small additive-blended triangle per
 * PoP, laid flat against the surface (facing outward) and colored by online
 * state. Single draw call regardless of count, which keeps headroom for arcs
 * and post FX.
 *
 * Supports hover/select picking via the instanced raycaster (the picked
 * instance's `id` is reported through the callbacks) and highlights the
 * hovered / selected marker. Pass these as children of `<Globe>` so they share
 * the globe's transform.
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import type { ThreeEvent } from '@react-three/fiber';
import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  type InstancedMesh as TInstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

export interface PopMarker {
  readonly lat: number;
  readonly lon: number;
  /** Stable id used for hover/select reporting and highlight matching. */
  readonly id?: string;
  /** Offline PoPs render dimmer / in the offline color. Default true. */
  readonly online?: boolean;
}

export interface PopsProps {
  readonly pops: readonly PopMarker[];
  /** Globe radius the markers sit on. Default 1. */
  readonly radius?: number;
  /** Marker radius in scene units. Default 0.026 * radius. */
  readonly size?: number;
  /** Color for online PoPs. */
  readonly color?: string | number;
  /** Color for offline PoPs. */
  readonly offlineColor?: string | number;
  /** Highlight color for the hovered marker. */
  readonly hoverColor?: string | number;
  /** Highlight color for the selected marker. */
  readonly selectedColor?: string | number;
  /** Currently-selected PoP id (rendered enlarged + highlighted). */
  readonly selectedId?: string | null;
  /** Currently-hovered PoP id (rendered highlighted). */
  readonly hoveredId?: string | null;
  /** Transparent-sort order; lower draws first (further back). Default 0. */
  readonly renderOrder?: number;
  /** Pointer entered/left a marker; reports its id (or null on leave). */
  readonly onHover?: (id: string | null) => void;
  /** A marker was clicked; reports its id. */
  readonly onSelect?: (id: string | null) => void;
  /** A click missed all markers (empty space / globe). */
  readonly onClear?: () => void;
}

// Markers float a hair above the surface so they're never z-clipped by it.
const SURFACE_LIFT = 1.012;
const SELECTED_SCALE = 1.7;

export function Pops({
  pops,
  radius = 1,
  size,
  color = 0xffc24b,
  offlineColor = 0x6b7480,
  hoverColor = 0xffe39c,
  selectedColor = 0xffffff,
  selectedId = null,
  hoveredId = null,
  renderOrder = 0,
  onHover,
  onSelect,
  onClear,
}: PopsProps): JSX.Element {
  const meshRef = useRef<TInstancedMesh>(null);
  const count = pops.length;
  const markerRadius = size ?? 0.03 * radius;

  // Inverted equilateral triangle in the local XY plane (circumradius 1, normal
  // +Z, tip at -Y so it points "down"). Each instance is oriented so +Z aligns
  // with the surface normal, laying it flat against the globe and facing outward.
  const geometry = useMemo(() => {
    const g = new BufferGeometry();
    g.setAttribute(
      'position',
      new Float32BufferAttribute([0, -1, 0, 0.866_025, 0.5, 0, -0.866_025, 0.5, 0], 3),
    );
    g.computeVertexNormals();
    return g;
  }, []);
  const material = useMemo(
    () =>
      new MeshBasicMaterial({
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
        side: DoubleSide,
      }),
    [],
  );

  const idAt = (instanceId: number | undefined): string | null =>
    instanceId == null ? null : (pops[instanceId]?.id ?? null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const matrix = new Matrix4();
    const online = new Color(color);
    const offline = new Color(offlineColor);
    const hover = new Color(hoverColor);
    const selected = new Color(selectedColor);

    // Reused basis vectors: w = outward normal, (u, v) span the tangent plane.
    const w = new Vector3();
    const u = new Vector3();
    const v = new Vector3();
    const up = new Vector3();
    const worldY = new Vector3(0, 1, 0);
    const worldX = new Vector3(1, 0, 0);

    for (let i = 0; i < count; i++) {
      const pop = pops[i]!;
      const isSelected = pop.id != null && pop.id === selectedId;
      const isHovered = pop.id != null && pop.id === hoveredId;
      const r = isSelected ? markerRadius * SELECTED_SCALE : markerRadius;
      const pos = latLonToVec3(pop.lat, pop.lon, radius * SURFACE_LIFT);

      // Orient the triangle flat on the surface: +Z → outward normal, tip
      // pointing roughly north. Near the poles fall back to a world-X up ref.
      w.copy(pos).normalize();
      up.copy(Math.abs(w.y) > 0.95 ? worldX : worldY);
      u.crossVectors(up, w).normalize();
      v.crossVectors(w, u);
      // Row-major: columns are the scaled basis (u, v, w) and the translation.
      matrix.set(
        u.x * r, v.x * r, w.x * r, pos.x,
        u.y * r, v.y * r, w.y * r, pos.y,
        u.z * r, v.z * r, w.z * r, pos.z,
        0, 0, 0, 1,
      );
      mesh.setMatrixAt(i, matrix);
      mesh.setColorAt(i, isSelected ? selected : isHovered ? hover : pop.online === false ? offline : online);
    }

    mesh.count = count;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [
    pops,
    count,
    radius,
    markerRadius,
    color,
    offlineColor,
    hoverColor,
    selectedColor,
    selectedId,
    hoveredId,
  ]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, Math.max(count, 1)]}
      renderOrder={renderOrder}
      onPointerMove={(e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        onHover?.(idAt(e.instanceId));
      }}
      onPointerOut={() => onHover?.(null)}
      onClick={(e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        onSelect?.(idAt(e.instanceId));
      }}
      onPointerMissed={() => onClear?.()}
    />
  );
}
